import React, { useEffect } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';

/**
 * Set on <html> once the loader has faded out. Consumed by app.css so an entrance
 * animation can wait for the screen to be visible; see the .scratch-reveal rules.
 */
const LOADER_DONE_CLASS = 'loader-done';

const InitialLoader = () => {
  const [isLoading, setIsLoading] = React.useState(true);

  const loaderRef = React.useRef<HTMLDivElement>(null);

  const closeLoader = () => {
    if (!loaderRef.current) return;

    // Scroll to top immediately when starting the fade out
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    gsap.to(loaderRef.current, {
      duration: 0.8,
      opacity: 0,
      ease: 'power4.inOut',
      onComplete: () => {
        // Remove inline overflow from html to allow natural scrolling (html becomes scrollable)
        // Set body to visible to override CSS rule and allow scrolling without dual scrollbars
        document.documentElement.style.overflow = '';
        document.body.style.overflow = 'visible';
        // Announce that the screen is uncovered. This loader sits over everything for
        // between 1.1s and 2.3s (300ms minimum display or the 1500ms fallback, plus the
        // 800ms fade), so ANY on-load animation started at first paint plays out behind it
        // and is never seen. CSS keys off this class to start instead of guessing a delay.
        document.documentElement.classList.add(LOADER_DONE_CLASS);
        // Use requestAnimationFrame to ensure the final frame is painted before unmounting
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsLoading(false);
          });
        });
      },
    });
  };

  useEffect(() => {
    // Set overflow hidden immediately on mount to prevent scrollbar flash
    // Set on html (the scrollable element) and body to prevent any scrolling
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    // Ensure initial opacity is set for smooth GSAP animation
    if (loaderRef.current) {
      gsap.set(loaderRef.current, { opacity: 1 });
    }

    const startTime = performance.now();
    const MIN_DISPLAY_TIME = 300;

    const fallbackTimeout = setTimeout(() => {
      closeLoader();
    }, 1500);

    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntriesByType(
        'resource',
      ) as PerformanceResourceTiming[];

      if (entries && entries.length > 0) {
        const pendingRequests = entries.filter(
          (entry) => entry.responseEnd === 0,
        );

        if (
          pendingRequests.length === 0 &&
          performance.now() - startTime >= MIN_DISPLAY_TIME
        ) {
          clearTimeout(fallbackTimeout);
          closeLoader();
          observer.disconnect();
        }
      }
    });

    observer.observe({ entryTypes: ['resource'] });

    // NOTE: LOADER_DONE_CLASS must NOT be set here. entry.client.tsx renders under
    // StrictMode, which runs effects mount -> cleanup -> mount, so anything in this
    // cleanup fires immediately on the first mount -- which announced the loader as
    // finished at 0ms and put every entrance animation back behind it. The 4s failsafe in
    // app.css covers the case where the fade never completes.
    return () => {
      clearTimeout(fallbackTimeout);
      observer.disconnect();
      // Remove inline overflow from html to allow natural scrolling
      // Set body to visible to override CSS rule and allow scrolling without dual scrollbars
      document.documentElement.style.overflow = '';
      document.body.style.overflow = 'visible';
    };
  }, []);

  if (!isLoading) return null;

  return (
    <StyledLoaderWrapper ref={loaderRef} className="loader-wrapper">
      <LogoWrapper className="loader">
        <LogoStyled
          src="/assets/icons/header-logo.svg"
          alt="Logo of broken planet"
          loading="eager"
          width={208}
          height={24}
        />
      </LogoWrapper>
    </StyledLoaderWrapper>
  );
};

const StyledLoaderWrapper = styled.div`
  position: fixed;
  width: 100vw;
  height: 100vh;
  z-index: ${({ theme }) => theme.zIndex.loader};
  background-color: ${({ theme }) => theme.colors.colorMain};
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  will-change: opacity, visibility;
  pointer-events: none;
`;

const LogoWrapper = styled.div`
  background-color: transparent;
  border: none;
  box-shadow: none;
  padding: 0;
  outline: none;

  &:focus {
    outline: none;
    box-shadow: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

const LogoStyled = styled.img`
  border: none;
  box-shadow: none;
  width: 208px;
  animation: pulseOpacity 2s ease-in-out infinite;

  @keyframes pulseOpacity {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
    }
    100% {
      opacity: 1;
    }
  }

  @media ${({ theme }) => theme.breakpoints.sm} {
    width: 140px;
  }
`;

export default InitialLoader;
